import { z } from "zod/v4";

import {
  AgenticLogicPriceFilterDescriptions,
  AgenticLogicProductServerWhereDescriptions,
  AgenticLogicStorefrontCategoryFilterDescriptions,
  AgenticLogicStorefrontMetafieldFilterDescriptions,
  AgenticLogicStorefrontPriceRangeFilterDescriptions,
  AgenticLogicStorefrontProductFilterDescriptions,
  AgenticLogicStorefrontVariantOptionFilterDescriptions,
  AgenticLogicStringAnyOfFilterDescriptions,
  AgenticLogicTagFilterDescriptions,
  AgenticLogicTitleFilterDescriptions,
} from "../../../descriptions/agentic-logic.describe";
import { assertDescriptions } from "../../../descriptions/types";

// ---------------------------------------------------------------------------
// Server-filterable predicates — everything the Storefront API evaluates
// server-side. The key set is pinned by AgenticLogicServerFilterKeyList (./filter-keys).
// ---------------------------------------------------------------------------

export const AgenticLogicTagFilterSchema = z
  .object({
    anyOf: z
      .array(z.string())
      .optional()
      .describe(AgenticLogicTagFilterDescriptions.anyOf),
    allOf: z
      .array(z.string())
      .optional()
      .describe(AgenticLogicTagFilterDescriptions.allOf),
  })
  .describe(AgenticLogicTagFilterDescriptions._schema);

export type AgenticLogicTagFilter = z.infer<typeof AgenticLogicTagFilterSchema>;

export const AgenticLogicStringAnyOfFilterSchema = z
  .object({
    anyOf: z
      .array(z.string())
      .describe(AgenticLogicStringAnyOfFilterDescriptions.anyOf),
  })
  .describe(AgenticLogicStringAnyOfFilterDescriptions._schema);

export type AgenticLogicStringAnyOfFilter = z.infer<
  typeof AgenticLogicStringAnyOfFilterSchema
>;

export const AgenticLogicPriceFilterSchema = z
  .object({
    gte: z
      .number()
      .int()
      .nonnegative()
      .optional()
      .describe(AgenticLogicPriceFilterDescriptions.gte),
    gt: z
      .number()
      .int()
      .nonnegative()
      .optional()
      .describe(AgenticLogicPriceFilterDescriptions.gt),
    lte: z
      .number()
      .int()
      .nonnegative()
      .optional()
      .describe(AgenticLogicPriceFilterDescriptions.lte),
    lt: z
      .number()
      .int()
      .nonnegative()
      .optional()
      .describe(AgenticLogicPriceFilterDescriptions.lt),
  })
  .describe(AgenticLogicPriceFilterDescriptions._schema);

export type AgenticLogicPriceFilter = z.infer<
  typeof AgenticLogicPriceFilterSchema
>;

export const AgenticLogicTitleFilterSchema = z
  .object({
    prefix: z
      .string()
      .optional()
      .describe(AgenticLogicTitleFilterDescriptions.prefix),
  })
  .strict()
  .describe(AgenticLogicTitleFilterDescriptions._schema);

export type AgenticLogicTitleFilter = z.infer<
  typeof AgenticLogicTitleFilterSchema
>;

export const AgenticLogicStorefrontVariantOptionFilterSchema = z
  .object({
    name: z
      .string()
      .describe(AgenticLogicStorefrontVariantOptionFilterDescriptions.name),
    value: z
      .string()
      .describe(AgenticLogicStorefrontVariantOptionFilterDescriptions.value),
  })
  .strict()
  .describe(AgenticLogicStorefrontVariantOptionFilterDescriptions._schema);

export type AgenticLogicStorefrontVariantOptionFilter = z.infer<
  typeof AgenticLogicStorefrontVariantOptionFilterSchema
>;

export const AgenticLogicStorefrontCategoryFilterSchema = z
  .object({
    id: z
      .string()
      .describe(AgenticLogicStorefrontCategoryFilterDescriptions.id),
  })
  .strict()
  .describe(AgenticLogicStorefrontCategoryFilterDescriptions._schema);

export type AgenticLogicStorefrontCategoryFilter = z.infer<
  typeof AgenticLogicStorefrontCategoryFilterSchema
>;

export const AgenticLogicStorefrontMetafieldFilterSchema = z
  .object({
    namespace: z
      .string()
      .describe(AgenticLogicStorefrontMetafieldFilterDescriptions.namespace),
    key: z
      .string()
      .describe(AgenticLogicStorefrontMetafieldFilterDescriptions.key),
    value: z
      .string()
      .describe(AgenticLogicStorefrontMetafieldFilterDescriptions.value),
  })
  .strict()
  .describe(AgenticLogicStorefrontMetafieldFilterDescriptions._schema);

export type AgenticLogicStorefrontMetafieldFilter = z.infer<
  typeof AgenticLogicStorefrontMetafieldFilterSchema
>;

export const AgenticLogicStorefrontPriceRangeFilterSchema = z
  .object({
    min: z
      .number()
      .int()
      .nonnegative()
      .optional()
      .describe(AgenticLogicStorefrontPriceRangeFilterDescriptions.min),
    max: z
      .number()
      .int()
      .nonnegative()
      .optional()
      .describe(AgenticLogicStorefrontPriceRangeFilterDescriptions.max),
  })
  .strict()
  .describe(AgenticLogicStorefrontPriceRangeFilterDescriptions._schema);

export type AgenticLogicStorefrontPriceRangeFilter = z.infer<
  typeof AgenticLogicStorefrontPriceRangeFilterSchema
>;

export const AgenticLogicStorefrontProductFilterSchema = z
  .object({
    available: z
      .boolean()
      .optional()
      .describe(AgenticLogicStorefrontProductFilterDescriptions.available),
    variantOption:
      AgenticLogicStorefrontVariantOptionFilterSchema.optional().describe(
        AgenticLogicStorefrontProductFilterDescriptions.variantOption
      ),
    category: AgenticLogicStorefrontCategoryFilterSchema.optional().describe(
      AgenticLogicStorefrontProductFilterDescriptions.category
    ),
    taxonomyMetafield:
      AgenticLogicStorefrontMetafieldFilterSchema.optional().describe(
        AgenticLogicStorefrontProductFilterDescriptions.taxonomyMetafield
      ),
    productType: z
      .string()
      .optional()
      .describe(AgenticLogicStorefrontProductFilterDescriptions.productType),
    productVendor: z
      .string()
      .optional()
      .describe(AgenticLogicStorefrontProductFilterDescriptions.productVendor),
    price: AgenticLogicStorefrontPriceRangeFilterSchema.optional().describe(
      AgenticLogicStorefrontProductFilterDescriptions.price
    ),
    productMetafield:
      AgenticLogicStorefrontMetafieldFilterSchema.optional().describe(
        AgenticLogicStorefrontProductFilterDescriptions.productMetafield
      ),
    variantMetafield:
      AgenticLogicStorefrontMetafieldFilterSchema.optional().describe(
        AgenticLogicStorefrontProductFilterDescriptions.variantMetafield
      ),
    tag: z
      .string()
      .optional()
      .describe(AgenticLogicStorefrontProductFilterDescriptions.tag),
  })
  .strict()
  .describe(AgenticLogicStorefrontProductFilterDescriptions._schema);

export type AgenticLogicStorefrontProductFilter = z.infer<
  typeof AgenticLogicStorefrontProductFilterSchema
>;

export const AgenticLogicProductServerWhereSchema = z
  .object({
    tags: AgenticLogicTagFilterSchema.optional().describe(
      AgenticLogicProductServerWhereDescriptions.tags
    ),
    productType: AgenticLogicStringAnyOfFilterSchema.optional().describe(
      AgenticLogicProductServerWhereDescriptions.productType
    ),
    vendor: AgenticLogicStringAnyOfFilterSchema.optional().describe(
      AgenticLogicProductServerWhereDescriptions.vendor
    ),
    price: AgenticLogicPriceFilterSchema.optional().describe(
      AgenticLogicProductServerWhereDescriptions.price
    ),
    title: AgenticLogicTitleFilterSchema.optional().describe(
      AgenticLogicProductServerWhereDescriptions.title
    ),
    productFilters: z
      .array(AgenticLogicStorefrontProductFilterSchema)
      .optional()
      .describe(AgenticLogicProductServerWhereDescriptions.productFilters),
  })
  .describe(AgenticLogicProductServerWhereDescriptions._schema);

export type AgenticLogicProductServerWhere = z.infer<
  typeof AgenticLogicProductServerWhereSchema
>;

// ---------------------------------------------------------------------------
// Compile-time checks: description objects must cover all schema keys
// ---------------------------------------------------------------------------

assertDescriptions<
  typeof AgenticLogicTagFilterDescriptions,
  AgenticLogicTagFilter
>();
assertDescriptions<
  typeof AgenticLogicStringAnyOfFilterDescriptions,
  AgenticLogicStringAnyOfFilter
>();
assertDescriptions<
  typeof AgenticLogicPriceFilterDescriptions,
  AgenticLogicPriceFilter
>();
assertDescriptions<
  typeof AgenticLogicTitleFilterDescriptions,
  AgenticLogicTitleFilter
>();
assertDescriptions<
  typeof AgenticLogicStorefrontVariantOptionFilterDescriptions,
  AgenticLogicStorefrontVariantOptionFilter
>();
assertDescriptions<
  typeof AgenticLogicStorefrontCategoryFilterDescriptions,
  AgenticLogicStorefrontCategoryFilter
>();
assertDescriptions<
  typeof AgenticLogicStorefrontMetafieldFilterDescriptions,
  AgenticLogicStorefrontMetafieldFilter
>();
assertDescriptions<
  typeof AgenticLogicStorefrontPriceRangeFilterDescriptions,
  AgenticLogicStorefrontPriceRangeFilter
>();
assertDescriptions<
  typeof AgenticLogicStorefrontProductFilterDescriptions,
  AgenticLogicStorefrontProductFilter
>();
assertDescriptions<
  typeof AgenticLogicProductServerWhereDescriptions,
  AgenticLogicProductServerWhere
>();
